export const enum InitialPageRequest {
  CONFIG = 'config',
  VARIANT = 'variant',
}

type TInitialRequestTiming = {
  startedAt: number
  completedAt: number
  actionDuration: number
  networkDuration: number | null
  ttfb: number | null
}

type TMeasureInitialPageRequestParams<T> = {
  name: InitialPageRequest
  resourcePath: string
  request: () => T | Promise<T>
}

const initialRequestTimings: Partial<
  Record<InitialPageRequest, TInitialRequestTiming>
> = {}

const getResourceTimings = ({
  resourcePath,
  startedAt,
  completedAt,
}: {
  resourcePath: string
  startedAt: number
  completedAt: number
}): PerformanceResourceTiming[] =>
  performance
    .getEntriesByType('resource')
    .filter(
      (entry): entry is PerformanceResourceTiming =>
        entry.entryType === 'resource' &&
        entry.name.includes(resourcePath) &&
        entry.startTime >= startedAt &&
        entry.startTime <= completedAt,
    )

const getMaxTtfb = (entries: PerformanceResourceTiming[]): number | null => {
  const ttfbValues = entries
    .map(({ requestStart, responseStart }) => responseStart - requestStart)
    .filter((ttfb) => ttfb > 0)

  return ttfbValues.length ? Math.round(Math.max(...ttfbValues)) : null
}

const getMaxNetworkDuration = (
  entries: PerformanceResourceTiming[],
): number | null => {
  const networkDurations = entries
    .map(({ duration }) => duration)
    .filter((duration) => duration > 0)

  return networkDurations.length
    ? Math.round(Math.max(...networkDurations))
    : null
}

export const measureInitialPageRequest = async <T>({
  name,
  resourcePath,
  request,
}: TMeasureInitialPageRequestParams<T>): Promise<T> => {
  const startedAt = performance.now()

  try {
    return await request()
  } finally {
    const completedAt = performance.now()
    const resourceTimings = getResourceTimings({
      resourcePath,
      startedAt,
      completedAt,
    })

    initialRequestTimings[name] = {
      startedAt,
      completedAt,
      actionDuration: Math.round(completedAt - startedAt),
      networkDuration: getMaxNetworkDuration(resourceTimings),
      ttfb: getMaxTtfb(resourceTimings),
    }
  }
}

export const getInitialPageRequestTimings = (): Partial<
  Record<InitialPageRequest, TInitialRequestTiming>
> => initialRequestTimings
